import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import styles from './Nav.module.css';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navLinksRef = useRef([]);
  const contactInfoRef = useRef(null);
  const footerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const timelineRef = useRef(null);

  const navItems = [
    { number: '01', label: 'פרויקטים', targetId: 'all-projects' },
    { number: '02', label: 'אודות', targetId: 'about' },
    { number: '03', label: 'צרו קשר', action: 'whatsapp' },
  ];

  useEffect(() => {
    timelineRef.current = gsap.timeline({ paused: true });

    const tl = timelineRef.current;

    tl.set(menuRef.current, { display: 'flex' })
      .fromTo(
        menuRef.current,
        { clipPath: 'inset(0 0 0 100%)' },
        { clipPath: 'inset(0 0 0 0%)', duration: 0.7, ease: 'power4.inOut' }
      )
      .fromTo(
        closeButtonRef.current,
        { opacity: 0, x: 20 },
        { opacity: 1, x: 0, duration: 0.4, ease: 'power3.out' },
        '-=0.3'
      )
      .fromTo(
        navLinksRef.current,
        { y: 80, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.08, duration: 0.6, ease: 'power3.out' },
        '-=0.2'
      )
      .fromTo(
        contactInfoRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' },
        '-=0.3'
      )
      .fromTo(
        footerRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.4, ease: 'power3.out' },
        '-=0.2'
      );

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, []);

  const openMenu = () => {
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
    timelineRef.current.play();
  };

  const closeMenu = () => {
    timelineRef.current.reverse();
    setTimeout(() => {
      setIsOpen(false);
      document.body.style.overflow = '';
    }, 700);
  };

  // Custom smooth scroll that recalculates target position each frame
  // to handle layout shifts from lazy-loaded images
  const animateScrollTo = (el, duration = 800) => {
    const startY = window.pageYOffset;
    let startTime = null;

    // Prevent scroll bouncing during animation
    document.documentElement.style.overscrollBehavior = 'none';

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeInOutCubic
      const ease = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      // Recalculate target each frame to adapt to layout shifts
      const targetY = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo(0, startY + (targetY - startY) * ease);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        // Final correction after animation - handles any remaining layout shifts
        document.documentElement.style.overscrollBehavior = '';
        setTimeout(() => {
          const finalY = el.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo(0, finalY);
        }, 100);
        setTimeout(() => {
          const finalY = el.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo(0, finalY);
        }, 500);
      }
    };

    requestAnimationFrame(step);
  };

  const scrollToSection = (targetId) => {
    timelineRef.current.reverse();
    setTimeout(() => {
      setIsOpen(false);
      document.body.style.overflow = '';
      const el = document.getElementById(targetId);
      if (el) {
        animateScrollTo(el);
      }
    }, 750);
  };

  const handleWhatsApp = () => {
    const phone = '972543324747';
    const message = encodeURIComponent('היי שירלי הגעתי מהדף שלך ואשמח לשמוע עוד על...');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    closeMenu();
  };

  const handleNavClick = (item) => {
    if (item.action === 'whatsapp') {
      handleWhatsApp();
    } else {
      scrollToSection(item.targetId);
    }
  };

  return (
    <>
      <nav className={styles.topBar}>
        <button
          className={styles.menuButton}
          onClick={openMenu}
          aria-label="פתח תפריט"
        >
          <span className={styles.menuIcon}>
            <span className={styles.menuLine}></span>
            <span className={styles.menuLine}></span>
          </span>
        </button>

        <a href="/" className={styles.logo}>
          <span className={styles.logoText}>SG</span>
          <span className={styles.logoDivider}></span>
          <span className={styles.logoSubtext}>Studio</span>
        </a>
      </nav>

      <div
        ref={menuRef}
        className={styles.fullscreenMenu}
        style={{ display: isOpen ? 'flex' : 'none' }}
      >
        <div className={styles.menuContent}>
          <button
            ref={closeButtonRef}
            className={styles.closeButton}
            onClick={closeMenu}
            aria-label="סגור תפריט"
          >
            <span className={styles.closeIcon}>
              <span className={styles.closeLine}></span>
              <span className={styles.closeLine}></span>
            </span>
          </button>

          <div className={styles.navSection}>
            <nav className={styles.navLinks}>
              {navItems.map((item, index) => (
                <button
                  key={item.number}
                  ref={(el) => (navLinksRef.current[index] = el)}
                  onClick={() => handleNavClick(item)}
                  className={styles.navLink}
                >
                  <span className={styles.navNumber}>{item.number}</span>
                  <span className={styles.navLabel}>{item.label}</span>
                  <span className={styles.navArrow}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M19 12H5M12 5l-7 7 7 7"/>
                    </svg>
                  </span>
                </button>
              ))}
            </nav>
          </div>

          <div ref={contactInfoRef} className={styles.contactSection}>
            <div className={styles.contactGroup}>
              <span className={styles.contactLabel}>טלפון</span>
              <a href="tel:+972543324747" className={styles.contactValue}>
                054-332-4747
              </a>
            </div>

            <div className={styles.contactGroup}>
              <span className={styles.contactLabel}>אימייל</span>
              <a href="mailto:shirlig2806@gmail.com" className={styles.contactValue}>
            shirlig2806@gmail.com
              </a>
            </div>

            <div className={styles.contactGroup}>
              <span className={styles.contactLabel}>עקבו</span>
              <div className={styles.socialLinks}>
                <a
                  href="https://www.instagram.com/shirli_goldshtein/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialLink}
                >
                  Instagram
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=100050354965168"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialLink}
                >
                  Facebook
                </a>
              </div>
            </div>
          </div>

          <div ref={footerRef} className={styles.menuFooter}>
            <span className={styles.footerText}>SG Studio</span>
            <span className={styles.footerDivider}></span>
            <span className={styles.footerText}>עיצוב פנים ואדריכלות</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
